function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

export const ELITE_BATCH23_PART13: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ─── 201. Budget Variance Alert ───────────────────────────────────────

  "Budget Variance Alert": {
    nodes: [
      trigger("Scheduled: Monthly budget variance check", "schedule", {
        schedule: "First business day of each month at 8:00 AM",
        howItStarts:
          "Runs on the first business day of each month to pull actual spending from QuickBooks and compare against the approved budget stored in Google Sheets.",
        setup:
          "Connect your QuickBooks account and configure the Google Sheets spreadsheet ID containing the approved monthly budgets by department.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "QuickBooks: Fetch month-end actual spending by department",
        240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://quickbooks.api.intuit.com/v3/company/{realm_id}/reports/ProfitAndLoss?start_date={prev_month_start}&end_date={prev_month_end}&summarize_column_by=Department",
            headers: [
              { key: "Authorization", value: "Bearer {quickbooks_token}" },
              { key: "Accept", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "Rows",
                customName: "department_actuals",
                type: "object",
              },
              {
                name: "Header.ReportName",
                customName: "report_name",
                type: "string",
              },
            ],
          },
        },
      ),
      n("3", "hitApi", "Google Sheets: Fetch approved budget figures", 480, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Budget!A2:D50",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "budget_rows", type: "object" },
          ],
        },
      }),
      n("4", "runCode", "Compute variance percentage per department", 720, {
        codeDetails: {
          language: "javascript",
          code: "const actuals = inputs.department_actuals; const budgets = inputs.budget_rows; const variances = []; for (const dept of budgets) { const actual = actuals[dept[0]] || 0; const budget = parseFloat(dept[1]); const variance_pct = ((actual - budget) / budget * 100).toFixed(1); variances.push({ department: dept[0], budget, actual, variance_pct: parseFloat(variance_pct) }); } const max_variance = Math.max(...variances.map(v => Math.abs(v.variance_pct))); return { variances, max_variance, over_budget_count: variances.filter(v => v.variance_pct > 10).length };",
        },
      }),
      n("5", "condition", "Check: Any department variance exceeds 10%?", 960, {
        conditionDetails: {
          conditions: [
            { variable: "{max_variance}", operator: ">", value: "10" },
          ],
        },
      }),
      slackNode(
        "6",
        "Slack: Alert #finance with variance summary",
        1200,
        "Budget Variance Alert for {prev_month_name}\n\n{over_budget_count} department(s) exceeded 10% variance.\n\nTop variances:\n{variance_summary}\n\nReview full report in Google Sheets: {sheet_url}",
      ),
      emailNode(
        "7",
        "Email: Send detailed variance report to department heads",
        1440,
        "{department_head_emails}",
        "Monthly Budget Variance Report — {prev_month_name}",
        "Hello,\n\nThe monthly budget variance report for {prev_month_name} is ready. Your department showed a variance of {dept_variance_pct}% against the approved budget.\n\nBudgeted: {dept_budget}\nActual: {dept_actual}\nVariance: {dept_variance_pct}%\n\nPlease review and submit any explanations for variances exceeding 10% by end of week.\n\nFull report: {sheet_url}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log variance report to audit trail",
        1680,
        "VarianceAudit",
        '[["{prev_month_name}","{timestamp}","{over_budget_count}","{max_variance}%","{total_actual}","{total_budget}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Monthly scheduled workflow that pulls actual spending from QuickBooks and budget targets from Google Sheets, computes department-level variances, alerts Slack and emails department heads when any variance exceeds 10%, and logs results to an audit trail.",
  },

  // ─── 202. Monthly Close Checklist ─────────────────────────────────────

  "Monthly Close Checklist": {
    nodes: [
      trigger("Scheduled: Month-end financial close kickoff", "schedule", {
        schedule: "Last business day of each month at 9:00 AM",
        howItStarts:
          "Triggers on the last business day of each month to generate and distribute the financial close checklist, creating tasks and tracking completion across the finance team.",
        setup:
          "Configure your Asana project ID for the finance close workspace and connect Slack for team notifications.",
        inputFields: [],
      }),
      n(
        "2",
        "runCode",
        "Generate month-end close task list with owners and deadlines",
        240,
        {
          codeDetails: {
            language: "javascript",
            code: "const month = new Date().toLocaleString('default', { month: 'long', year: 'numeric' }); const tasks = [ { name: 'Reconcile bank accounts', owner: 'controller', days: 2 }, { name: 'Post depreciation entries', owner: 'staff_accountant', days: 3 }, { name: 'Review AP/AR aging', owner: 'ap_manager', days: 2 }, { name: 'Accrue outstanding expenses', owner: 'controller', days: 3 }, { name: 'Reconcile intercompany', owner: 'senior_accountant', days: 4 }, { name: 'Prepare trial balance', owner: 'controller', days: 5 }, { name: 'Review revenue recognition', owner: 'revenue_analyst', days: 4 }, { name: 'Submit close package', owner: 'controller', days: 5 } ]; return { month, tasks, task_count: tasks.length };",
          },
        },
      ),
      n(
        "3",
        "hitApi",
        "Asana: Create close checklist project with subtasks",
        480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://app.asana.com/api/1.0/tasks",
            headers: [
              { key: "Authorization", value: "Bearer {asana_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "data",
                value:
                  '{"name":"Monthly Close — {month}","projects":["{finance_project_id}"],"notes":"Auto-generated close checklist with {task_count} tasks."}',
              },
            ],
            variables: [
              { name: "data.gid", customName: "close_task_id", type: "string" },
            ],
          },
        },
      ),
      slackNode(
        "4",
        "Slack: Notify #finance team of close kickoff",
        720,
        "Monthly Close Kickoff — {month}\n\nThe month-end close checklist has been created with {task_count} tasks.\n\nAsana project: {asana_project_url}\n\nKey deadlines:\n- Bank reconciliation: Day 2\n- Trial balance: Day 5\n- Close package submission: Day 5\n\nPlease check Asana for your assigned tasks.",
      ),
      n("5", "delayAction", "Wait 3 business days for task completion", 960, {
        delayDetails: { duration: 3, unit: "days" },
      }),
      n(
        "6",
        "runCode",
        "Check task completion status and compute progress",
        1200,
        {
          codeDetails: {
            language: "javascript",
            code: "const completed = inputs.tasks_completed || 0; const total = inputs.task_count; const pct = Math.round((completed / total) * 100); const overdue = inputs.overdue_tasks || []; return { pct_complete: pct, overdue_count: overdue.length, all_complete: pct === 100 };",
          },
        },
      ),
      n("7", "condition", "Check: All close tasks completed?", 1440, {
        conditionDetails: {
          conditions: [
            { variable: "{all_complete}", operator: "==", value: "true" },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Send close confirmation or overdue escalation to CFO",
        1680,
        "{cfo_email}",
        "Monthly Close Status — {month}: {pct_complete}% Complete",
        "Hello,\n\nThe {month} month-end close is {pct_complete}% complete.\n\n{overdue_count} task(s) are overdue and require attention.\n\nOverdue items:\n{overdue_task_list}\n\nPlease follow up with the assigned owners to ensure timely close.\n\nAsana project: {asana_project_url}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Kicks off month-end financial close by generating a checklist with assigned owners, creating Asana tasks, notifying the finance team via Slack, waiting 3 days, then checking completion and escalating overdue items to the CFO.",
  },

  // ─── 203. AR Reminder Sequence ────────────────────────────────────────

  "AR Reminder Sequence": {
    nodes: [
      trigger("Scheduled: Daily accounts receivable aging check", "schedule", {
        schedule: "Every business day at 9:00 AM",
        howItStarts:
          "Runs daily to fetch overdue invoices from QuickBooks, classify them by aging bucket, and trigger reminder emails to customers with outstanding balances.",
        setup:
          "Connect your QuickBooks account and configure email templates for each aging bucket (1-30, 31-60, 60+ days).",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "QuickBooks: Fetch all overdue invoices with aging details",
        240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://quickbooks.api.intuit.com/v3/company/{realm_id}/reports/AgedReceivables?date_macro=Today",
            headers: [
              { key: "Authorization", value: "Bearer {quickbooks_token}" },
              { key: "Accept", value: "application/json" },
            ],
            body: [],
            variables: [
              { name: "Rows", customName: "overdue_invoices", type: "object" },
              {
                name: "Header.TotalAR",
                customName: "total_ar_outstanding",
                type: "string",
              },
            ],
          },
        },
      ),
      n("3", "runCode", "Classify invoices by aging bucket and priority", 480, {
        codeDetails: {
          language: "javascript",
          code: "const invoices = inputs.overdue_invoices; const buckets = { early: [], mid: [], late: [] }; for (const inv of invoices) { const days = inv.days_overdue; if (days <= 30) buckets.early.push(inv); else if (days <= 60) buckets.mid.push(inv); else buckets.late.push(inv); } return { early_count: buckets.early.length, mid_count: buckets.mid.length, late_count: buckets.late.length, has_late: buckets.late.length > 0, total_overdue: invoices.length };",
        },
      }),
      n("4", "condition", "Check: Any invoices overdue > 30 days?", 720, {
        conditionDetails: {
          conditions: [
            { variable: "{has_late}", operator: "==", value: "true" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Send payment reminder to overdue customers",
        960,
        "{customer_email}",
        "Payment Reminder: Invoice #{invoice_number} — {days_overdue} days overdue",
        "Hello {customer_name},\n\nThis is a friendly reminder that Invoice #{invoice_number} for {invoice_amount} was due on {due_date} and is now {days_overdue} days overdue.\n\nOutstanding balance: {balance_due}\n\nPlease remit payment at your earliest convenience. If you've already sent payment, please disregard this notice.\n\nPayment portal: {payment_link}\n\nQuestions? Reply to this email or contact our billing team.",
      ),
      n("6", "delayAction", "Wait 14 days for payment", 1200, {
        delayDetails: { duration: 14, unit: "days" },
      }),
      emailNode(
        "7",
        "Email: Send escalated reminder with account hold warning",
        1440,
        "{customer_email}",
        "Urgent: Invoice #{invoice_number} — {days_overdue} days overdue, action required",
        "Hello {customer_name},\n\nWe have not received payment for Invoice #{invoice_number} ({invoice_amount}), which is now {days_overdue} days past due.\n\nTo avoid service interruption, please remit payment within the next 7 business days.\n\nPay now: {payment_link}\n\nIf you are experiencing payment difficulties, please contact our billing team immediately so we can discuss options.",
      ),
      slackNode(
        "8",
        "Slack: Alert account owner about chronically overdue invoice",
        1680,
        "AR Escalation: Invoice #{invoice_number} for {customer_name} is {days_overdue} days overdue ({invoice_amount}).\n\nAccount owner: {account_owner}\nTotal AR outstanding: {total_ar_outstanding}\n\nPlease follow up directly or escalate to collections.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Daily check of QuickBooks aged receivables, classifies invoices by aging bucket, sends polite payment reminders for overdue invoices, waits 14 days, sends an escalated warning, and alerts the account owner in Slack for persistent non-payment.",
  },

  // ─── 204. AP Capture to Accounting System ─────────────────────────────

  "AP Capture to Accounting System": {
    nodes: [
      trigger("Email trigger: Invoice received at invoices@ inbox", "email", {
        source: "email",
        event: "invoice_received",
        howItStarts:
          "Fires when a new email arrives at the invoices@ inbox, capturing the sender, subject line, body text, and any PDF attachments for invoice processing.",
        setup:
          "Configure the invoices@ email address as a monitored inbox. Ensure PDF attachment parsing is enabled.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract vendor, amount, due date, and line items from invoice",
        240,
        "You are an accounts payable specialist. Extract the vendor name, vendor email, invoice number, invoice date, due date, total amount, currency, payment terms, and line items from the invoice email and attachment text. Also identify the GL account category. Return vendor_name, vendor_email, invoice_number, invoice_date, due_date, total_amount, currency, payment_terms, line_items, gl_category.",
        "Extract AP details from this invoice email: Subject: {email_subject}, Body: {email_body}, Attachment text: {attachment_text}",
        [
          { field: "vendor_name", as: "vendor_name", type: "string" },
          { field: "invoice_number", as: "invoice_number", type: "string" },
          { field: "due_date", as: "due_date", type: "string" },
          { field: "total_amount", as: "total_amount", type: "string" },
          { field: "payment_terms", as: "payment_terms", type: "string" },
          { field: "gl_category", as: "gl_category", type: "string" },
        ],
      ),
      n("3", "runCode", "Match invoice to existing purchase order", 480, {
        codeDetails: {
          language: "javascript",
          code: "const invoice_num = inputs.invoice_number; const vendor = inputs.vendor_name; const amount = parseFloat(inputs.total_amount); const po_match = inputs.purchase_orders?.find(po => po.vendor === vendor && Math.abs(po.amount - amount) < 0.01); return { po_matched: !!po_match, po_number: po_match?.po_number || 'NONE', po_amount: po_match?.amount || 0, amount_variance: po_match ? Math.abs(po_match.amount - amount).toFixed(2) : 'N/A', is_new_vendor: !inputs.known_vendors?.includes(vendor) };",
        },
      }),
      n(
        "4",
        "condition",
        "Check: Invoice matches existing purchase order?",
        720,
        {
          conditionDetails: {
            conditions: [
              { variable: "{po_matched}", operator: "==", value: "true" },
            ],
          },
        },
      ),
      n("5", "hitApi", "QuickBooks: Create bill entry with GL coding", 960, {
        hitApiDetails: {
          method: "POST",
          url: "https://quickbooks.api.intuit.com/v3/company/{realm_id}/bill",
          headers: [
            { key: "Authorization", value: "Bearer {quickbooks_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "VendorRef", value: '{"value":"{vendor_id}"}' },
            { key: "TxnDate", value: "{invoice_date}" },
            { key: "DueDate", value: "{due_date}" },
            { key: "TotalAmt", value: "{total_amount}" },
            { key: "DocNumber", value: "{invoice_number}" },
          ],
          variables: [
            { name: "Bill.Id", customName: "bill_id", type: "string" },
            {
              name: "Bill.TotalAmt",
              customName: "bill_amount",
              type: "string",
            },
          ],
        },
      }),
      n("6", "approvalGate", "Approval: AP manager reviews bill entry", 1200, {
        approvalDetails: {
          approvers: ["ap_manager"],
          instructions:
            "Review the auto-captured invoice for {vendor_name}: Invoice #{invoice_number}, Amount: {total_amount}, PO: {po_number}. Verify GL coding and approve for payment.",
        },
      }),
      emailNode(
        "7",
        "Email: Send processing confirmation to vendor",
        1440,
        "{vendor_email}",
        "Invoice #{invoice_number} received and processed",
        "Hello,\n\nWe have received and processed your Invoice #{invoice_number} for {total_amount}.\n\nPayment terms: {payment_terms}\nExpected payment date: {due_date}\n\nIf you have questions about this invoice, please reference Invoice #{invoice_number} in your correspondence.\n\nThank you for your business.",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log AP entry to invoice register",
        1680,
        "APRegister",
        '[["{timestamp}","{vendor_name}","{invoice_number}","{total_amount}","{due_date}","{po_number}","{gl_category}","{bill_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Captures invoices arriving by email, uses AI to extract vendor and line-item details, matches against purchase orders, creates a QuickBooks bill entry, routes for AP manager approval, confirms receipt to the vendor, and logs to the invoice register.",
  },

  // ─── 205. Receipt OCR to Ledger Coding ────────────────────────────────

  "Receipt OCR to Ledger Coding": {
    nodes: [
      trigger("Google Drive webhook: Receipt image uploaded", "webhook_app", {
        source: "google_drive",
        event: "file.create",
        howItStarts:
          "Fires when a receipt image or PDF is uploaded to the designated Google Drive receipts folder, providing the file ID, name, MIME type, and uploader email.",
        setup:
          "Connect Google Drive and configure the webhook to monitor the receipts upload folder. Supported formats: JPEG, PNG, PDF.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: OCR extract merchant, amount, date, and category from receipt",
        240,
        "You are an expense accounting specialist with OCR capability. Extract the merchant name, transaction date, total amount, tax amount, currency, payment method, and receipt items from the receipt image/text. Auto-assign the most likely GL account code based on the merchant category. Return merchant, transaction_date, total_amount, tax_amount, currency, payment_method, items, gl_account_code, gl_account_name, confidence_score.",
        "Extract receipt details and assign GL coding from this receipt: {receipt_text_or_url}",
        [
          { field: "merchant", as: "merchant", type: "string" },
          { field: "transaction_date", as: "transaction_date", type: "string" },
          { field: "total_amount", as: "total_amount", type: "string" },
          { field: "tax_amount", as: "tax_amount", type: "string" },
          { field: "gl_account_code", as: "gl_account_code", type: "string" },
          { field: "gl_account_name", as: "gl_account_name", type: "string" },
          { field: "confidence_score", as: "confidence_score", type: "number" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Auto-code GL account and validate against chart of accounts",
        480,
        {
          codeDetails: {
            language: "javascript",
            code: "const gl_code = inputs.gl_account_code; const confidence = inputs.confidence_score; const valid_codes = inputs.chart_of_accounts || []; const is_valid = valid_codes.includes(gl_code); return { gl_valid: is_valid, high_confidence: confidence >= 90, needs_review: !is_valid || confidence < 90, coding_status: (is_valid && confidence >= 90) ? 'auto-coded' : 'needs-review' };",
          },
        },
      ),
      n("4", "condition", "Check: GL coding confidence >= 90%?", 720, {
        conditionDetails: {
          conditions: [
            { variable: "{high_confidence}", operator: "==", value: "true" },
          ],
        },
      }),
      n(
        "5",
        "hitApi",
        "QuickBooks: Create expense entry with auto-coded GL account",
        960,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://quickbooks.api.intuit.com/v3/company/{realm_id}/purchase",
            headers: [
              { key: "Authorization", value: "Bearer {quickbooks_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "PaymentType", value: "Cash" },
              { key: "TotalAmt", value: "{total_amount}" },
              { key: "TxnDate", value: "{transaction_date}" },
              { key: "EntityRef", value: '{"value":"{merchant}"}' },
              {
                key: "Line",
                value:
                  '[{"Amount":"{total_amount}","DetailType":"AccountBasedExpenseLineDetail","AccountBasedExpenseLineDetail":{"AccountRef":{"value":"{gl_account_code}"}}}]',
              },
            ],
            variables: [
              { name: "Purchase.Id", customName: "expense_id", type: "string" },
            ],
          },
        },
      ),
      emailNode(
        "6",
        "Email: Confirm expense entry to the uploader",
        1200,
        "{uploader_email}",
        "Receipt processed: {merchant} — {total_amount}",
        "Your receipt has been processed and coded automatically.\n\nMerchant: {merchant}\nDate: {transaction_date}\nAmount: {total_amount}\nGL Account: {gl_account_name} ({gl_account_code})\nConfidence: {confidence_score}%\n\nQuickBooks entry ID: {expense_id}\n\nIf the coding is incorrect, please reply to this email with the correct GL account.",
      ),
      slackNode(
        "7",
        "Slack: Flag low-confidence receipts for manual review",
        1440,
        "Receipt Review Needed\n\nMerchant: {merchant}\nAmount: {total_amount}\nAuto-assigned GL: {gl_account_name} ({gl_account_code})\nConfidence: {confidence_score}%\n\nThis receipt needs manual GL coding review. Please update in QuickBooks: {quickbooks_url}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log receipt processing to expense register",
        1680,
        "ReceiptLog",
        '[["{timestamp}","{merchant}","{total_amount}","{transaction_date}","{gl_account_code}","{confidence_score}","{coding_status}","{expense_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "8"),
    ],
    summary:
      "Captures receipt uploads from Google Drive, uses AI OCR to extract merchant/amount/date and auto-assign GL codes, creates QuickBooks expense entries for high-confidence matches, flags low-confidence receipts in Slack for manual review, and logs all processing to a spreadsheet.",
  },

  // ─── 206. Revenue Recognition Prep Workflow ───────────────────────────

  "Revenue Recognition Prep Workflow": {
    nodes: [
      trigger(
        "Scheduled: Monthly revenue recognition preparation",
        "schedule",
        {
          schedule: "3rd business day of each month at 7:00 AM",
          howItStarts:
            "Runs on the 3rd business day of each month to pull all closed deals from HubSpot, apply ASC 606 revenue recognition rules, and prepare journal entries for the controller to review.",
          setup:
            "Connect HubSpot CRM and QuickBooks. Configure revenue recognition rules (ratably over contract term, milestone-based, or point-in-time) in the workflow settings.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "HubSpot: Fetch all deals closed in the previous month",
        240,
        {
          hitApiDetails: {
            method: "GET",
            url: 'https://api.hubapi.com/crm/v3/objects/deals?properties=dealname,amount,closedate,contract_term,revenue_type,pipeline&filterGroups=[{"filters":[{"propertyName":"closedate","operator":"BETWEEN","value":"{prev_month_start}","highValue":"{prev_month_end}"},{"propertyName":"dealstage","operator":"EQ","value":"closedwon"}]}]&limit=200',
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              { name: "results", customName: "closed_deals", type: "object" },
              { name: "total", customName: "deal_count", type: "number" },
            ],
          },
        },
      ),
      n("3", "runCode", "Apply revenue recognition rules per deal", 480, {
        codeDetails: {
          language: "javascript",
          code: "const deals = inputs.closed_deals; const entries = []; let total_recognized = 0; let total_deferred = 0; for (const deal of deals) { const amount = deal.amount; const term = deal.contract_term || 1; const monthly = (amount / term).toFixed(2); const recognized = parseFloat(monthly); const deferred = amount - recognized; entries.push({ deal: deal.dealname, total: amount, recognized, deferred, type: term > 1 ? 'ratable' : 'point-in-time' }); total_recognized += recognized; total_deferred += deferred; } return { entries, total_recognized: total_recognized.toFixed(2), total_deferred: total_deferred.toFixed(2), has_deferred: total_deferred > 0 };",
        },
      }),
      n("4", "condition", "Check: Deferred revenue entries exist?", 720, {
        conditionDetails: {
          conditions: [
            { variable: "{has_deferred}", operator: "==", value: "true" },
          ],
        },
      }),
      n(
        "5",
        "hitApi",
        "QuickBooks: Create journal entries for recognized and deferred revenue",
        960,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://quickbooks.api.intuit.com/v3/company/{realm_id}/journalentry",
            headers: [
              { key: "Authorization", value: "Bearer {quickbooks_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "TxnDate", value: "{prev_month_end}" },
              { key: "DocNumber", value: "REVREC-{prev_month_code}" },
              {
                key: "Line",
                value:
                  '[{"JournalEntryLineDetail":{"PostingType":"Credit","AccountRef":{"value":"revenue_account"}},"Amount":"{total_recognized}"},{"JournalEntryLineDetail":{"PostingType":"Credit","AccountRef":{"value":"deferred_revenue_account"}},"Amount":"{total_deferred}"}]',
              },
            ],
            variables: [
              {
                name: "JournalEntry.Id",
                customName: "journal_entry_id",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "6",
        "approvalGate",
        "Approval: Controller reviews revenue recognition entries",
        1200,
        {
          approvalDetails: {
            approvers: ["controller"],
            instructions:
              "Review the monthly revenue recognition journal entries.\n\nTotal recognized: {total_recognized}\nTotal deferred: {total_deferred}\nDeals processed: {deal_count}\nJournal entry: REVREC-{prev_month_code}\n\nApprove to post entries or reject with corrections.",
          },
        },
      ),
      emailNode(
        "7",
        "Email: Send rev-rec summary to finance leadership",
        1440,
        "{finance_leadership_email}",
        "Revenue Recognition Summary — {prev_month_name}",
        "Monthly Revenue Recognition Report\n\nPeriod: {prev_month_name}\nDeals processed: {deal_count}\n\nRecognized revenue: {total_recognized}\nDeferred revenue: {total_deferred}\n\nJournal entry ID: {journal_entry_id}\nStatus: Approved by controller\n\nFull breakdown available in the attached schedule.",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log rev-rec entries to compliance schedule",
        1680,
        "RevRecSchedule",
        '[["{prev_month_name}","{deal_count}","{total_recognized}","{total_deferred}","{journal_entry_id}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Monthly workflow that fetches closed deals from HubSpot, applies revenue recognition rules to compute recognized vs deferred amounts, creates QuickBooks journal entries, routes to controller for approval, and logs the schedule for compliance.",
  },

  // ─── 207. Renewal Quote Generation ────────────────────────────────────

  "Renewal Quote Generation": {
    nodes: [
      trigger(
        "Scheduled: Daily renewal quote generation for upcoming renewals",
        "schedule",
        {
          schedule: "Every business day at 8:00 AM",
          howItStarts:
            "Runs daily to identify HubSpot deals with renewal dates within 60 days, computes updated pricing, generates renewal quotes via AI, and routes price changes for approval.",
          setup:
            "Connect HubSpot CRM with deal properties including renewal_date and contract_value. Configure pricing rules and approval thresholds.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "HubSpot: Fetch deals with renewals due in next 60 days",
        240,
        {
          hitApiDetails: {
            method: "GET",
            url: 'https://api.hubapi.com/crm/v3/objects/deals?properties=dealname,amount,renewal_date,hubspot_owner_id,contract_term,associated_company&filterGroups=[{"filters":[{"propertyName":"renewal_date","operator":"BETWEEN","value":"{today}","highValue":"{today_plus_60}"}]}]&limit=100',
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              { name: "results", customName: "renewal_deals", type: "object" },
              { name: "total", customName: "renewal_count", type: "number" },
            ],
          },
        },
      ),
      n(
        "3",
        "runCode",
        "Compute renewal pricing with uplift and discount rules",
        480,
        {
          codeDetails: {
            language: "javascript",
            code: "const deals = inputs.renewal_deals; const quotes = []; for (const deal of deals) { const current = deal.amount; const uplift = current * 0.05; const renewal_price = (current + uplift).toFixed(2); const has_price_change = uplift > 0; quotes.push({ deal_name: deal.dealname, current_price: current, renewal_price, uplift_pct: '5%', renewal_date: deal.renewal_date, owner_id: deal.hubspot_owner_id, has_price_change }); } return { quotes, total_renewal_value: quotes.reduce((s, q) => s + parseFloat(q.renewal_price), 0).toFixed(2), has_any_price_change: quotes.some(q => q.has_price_change) };",
          },
        },
      ),
      aiNode(
        "4",
        "OpenAI: Draft personalized renewal quote email for each deal",
        720,
        "You are a B2B renewal specialist. Given deal details including current price, renewal price, contract term, and customer name, draft a professional and value-focused renewal quote email. Emphasize the value delivered during the current term and frame any price increase positively. Return subject_line, email_body, value_summary.",
        "Draft renewal quote for: Deal: {deal_name}, Current: {current_price}, Renewal: {renewal_price}, Term: {contract_term}, Renewal date: {renewal_date}",
        [
          { field: "subject_line", as: "quote_subject", type: "string" },
          { field: "email_body", as: "quote_body", type: "string" },
          { field: "value_summary", as: "value_summary", type: "string" },
        ],
      ),
      n(
        "5",
        "condition",
        "Check: Quote includes a price change requiring approval?",
        960,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "{has_any_price_change}",
                operator: "==",
                value: "true",
              },
            ],
          },
        },
      ),
      n(
        "6",
        "approvalGate",
        "Approval: Rep and manager review renewal quote with price change",
        1200,
        {
          approvalDetails: {
            approvers: ["deal_owner", "sales_manager"],
            instructions:
              "Review renewal quote for {deal_name}.\n\nCurrent price: {current_price}\nRenewal price: {renewal_price} ({uplift_pct} uplift)\nRenewal date: {renewal_date}\n\nApprove to send the quote to the customer.",
          },
        },
      ),
      emailNode(
        "7",
        "Email: Send renewal quote to customer",
        1440,
        "{customer_email}",
        "{quote_subject}",
        "{quote_body}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log renewal quote to pipeline tracker",
        1680,
        "RenewalTracker",
        '[["{timestamp}","{deal_name}","{current_price}","{renewal_price}","{renewal_date}","{quote_status}","{deal_owner}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Daily scan of HubSpot for upcoming renewals within 60 days, computes renewal pricing with uplift rules, uses AI to draft personalized quote emails, routes price changes through rep/manager approval, sends the quote, and logs to a renewal tracker.",
  },

  // ─── 208. Sales Commission Data Sync ──────────────────────────────────

  "Sales Commission Data Sync": {
    nodes: [
      trigger("HubSpot webhook: Deal marked as closed-won", "webhook_app", {
        source: "hubspot",
        event: "deal.propertyChange",
        howItStarts:
          "Fires when a HubSpot deal stage changes to closed-won, providing the deal ID, amount, owner, close date, and associated company for commission calculation.",
        setup:
          "Connect HubSpot and enable the deal.propertyChange webhook filtered for dealstage = closedwon.",
        inputFields: [],
      }),
      n(
        "2",
        "runCode",
        "Compute commission amount based on deal size and rep tier",
        240,
        {
          codeDetails: {
            language: "javascript",
            code: "const amount = inputs.deal_amount; const rep_tier = inputs.rep_tier || 'standard'; const rates = { standard: 0.08, senior: 0.10, enterprise: 0.12 }; const rate = rates[rep_tier] || 0.08; const commission = (amount * rate).toFixed(2); const is_split = inputs.split_reps && inputs.split_reps.length > 1; return { commission, rate_pct: (rate * 100) + '%', is_split, rep_name: inputs.deal_owner_name, deal_name: inputs.deal_name, deal_amount: amount };",
          },
        },
      ),
      sheetsNode(
        "3",
        "Google Sheets: Append commission record to payroll sheet",
        480,
        "Commissions",
        '[["{timestamp}","{rep_name}","{deal_name}","{deal_amount}","{rate_pct}","{commission}","{close_date}","{is_split}"]]',
      ),
      n(
        "4",
        "condition",
        "Check: Is this a split deal between multiple reps?",
        720,
        {
          conditionDetails: {
            conditions: [
              { variable: "{is_split}", operator: "==", value: "true" },
            ],
          },
        },
      ),
      n("5", "runCode", "Compute split commission amounts per rep", 960, {
        codeDetails: {
          language: "javascript",
          code: "const total_commission = parseFloat(inputs.commission); const reps = inputs.split_reps; const splits = reps.map((rep, i) => { const pct = rep.split_pct || (100 / reps.length); const amount = (total_commission * pct / 100).toFixed(2); return { name: rep.name, email: rep.email, split_pct: pct, commission_amount: amount }; }); return { splits, split_summary: splits.map(s => s.name + ': $' + s.commission_amount + ' (' + s.split_pct + '%)').join(', ') };",
        },
      }),
      slackNode(
        "6",
        "Slack: Notify rep(s) of earned commission",
        1200,
        "Commission Earned!\n\nDeal: {deal_name}\nAmount: ${deal_amount}\nCommission: ${commission} ({rate_pct})\nRep: {rep_name}\n{split_summary}\n\nCongratulations! This will be included in your next payroll cycle.",
      ),
      emailNode(
        "7",
        "Email: Send monthly commission summary to rep",
        1440,
        "{rep_email}",
        "Commission Earned: {deal_name} — ${commission}",
        "Hello {rep_name},\n\nCongratulations on closing {deal_name}!\n\nDeal amount: ${deal_amount}\nCommission rate: {rate_pct}\nCommission earned: ${commission}\n\nThis commission will be included in your next monthly payroll. View your full commission statement in the Google Sheets tracker.\n\nKeep up the great work!",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log commission reconciliation entry",
        1680,
        "CommissionReconciliation",
        '[["{timestamp}","{deal_name}","{deal_amount}","{commission}","{is_split}","{split_summary}","{payroll_period}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Triggers on closed-won deals in HubSpot, computes commission based on rep tier and deal size, handles split deals between multiple reps, logs to a commissions payroll sheet, notifies reps via Slack and email, and maintains a reconciliation record.",
  },

  // ─── 209. Forecast Rollup Digest ──────────────────────────────────────

  "Forecast Rollup Digest": {
    nodes: [
      trigger("Scheduled: Weekly pipeline forecast rollup", "schedule", {
        schedule: "Every Monday at 7:00 AM",
        howItStarts:
          "Runs every Monday morning to pull the latest pipeline data from HubSpot, compute weighted forecast by stage, generate an AI narrative summary, and distribute to leadership.",
        setup:
          "Connect HubSpot CRM. Configure deal stage weightings (e.g., Proposal: 50%, Negotiation: 75%, Verbal Commit: 90%) and quota targets.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "HubSpot: Fetch full active pipeline with stage and amount data",
        240,
        {
          hitApiDetails: {
            method: "GET",
            url: 'https://api.hubapi.com/crm/v3/objects/deals?properties=dealname,amount,dealstage,closedate,hubspot_owner_id,pipeline&filterGroups=[{"filters":[{"propertyName":"dealstage","operator":"NOT_IN","values":["closedwon","closedlost"]}]}]&limit=500',
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              { name: "results", customName: "active_deals", type: "object" },
              {
                name: "total",
                customName: "active_deal_count",
                type: "number",
              },
            ],
          },
        },
      ),
      n("3", "runCode", "Compute weighted forecast by stage and rep", 480, {
        codeDetails: {
          language: "javascript",
          code: "const deals = inputs.active_deals; const weights = { discovery: 0.1, qualification: 0.2, demo: 0.3, proposal: 0.5, negotiation: 0.75, verbal_commit: 0.9 }; let total_weighted = 0; let total_pipeline = 0; const by_stage = {}; for (const d of deals) { const w = weights[d.dealstage] || 0.1; const weighted = d.amount * w; total_weighted += weighted; total_pipeline += d.amount; by_stage[d.dealstage] = (by_stage[d.dealstage] || 0) + weighted; } const quota = inputs.quarterly_quota || 1000000; const pct_to_quota = ((total_weighted / quota) * 100).toFixed(1); const shortfall = total_weighted < quota; return { total_weighted: total_weighted.toFixed(0), total_pipeline: total_pipeline.toFixed(0), pct_to_quota, shortfall, by_stage };",
        },
      }),
      aiNode(
        "4",
        "OpenAI: Generate narrative forecast summary with insights",
        720,
        "You are a sales operations analyst. Given pipeline data including total weighted forecast, pipeline by stage, percentage to quota, and deal count, write a concise executive forecast summary (3-4 paragraphs). Highlight key risks, opportunities, and recommended actions. Return narrative, key_risks, key_opportunities, recommended_actions.",
        "Generate forecast narrative: Total weighted: {total_weighted}, Pipeline: {total_pipeline}, Deals: {active_deal_count}, Quota attainment: {pct_to_quota}%, By stage: {by_stage}",
        [
          { field: "narrative", as: "forecast_narrative", type: "string" },
          { field: "key_risks", as: "key_risks", type: "string" },
          {
            field: "key_opportunities",
            as: "key_opportunities",
            type: "string",
          },
          {
            field: "recommended_actions",
            as: "recommended_actions",
            type: "string",
          },
        ],
      ),
      emailNode(
        "5",
        "Email: Send forecast digest to sales leadership",
        960,
        "{sales_leadership_emails}",
        "Weekly Pipeline Forecast — {pct_to_quota}% to Quota",
        "Weekly Pipeline Forecast Report\n\nTotal Pipeline: ${total_pipeline}\nWeighted Forecast: ${total_weighted}\nQuota Attainment: {pct_to_quota}%\nActive Deals: {active_deal_count}\n\n{forecast_narrative}\n\nKey Risks:\n{key_risks}\n\nKey Opportunities:\n{key_opportunities}\n\nRecommended Actions:\n{recommended_actions}",
      ),
      slackNode(
        "6",
        "Slack: Post forecast highlights to #sales-leadership",
        1200,
        "Weekly Forecast Update\n\nPipeline: ${total_pipeline} | Weighted: ${total_weighted}\nQuota: {pct_to_quota}% attainment | {active_deal_count} active deals\n\n{forecast_narrative}\n\nFull report sent to email.",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Update forecast dashboard with weekly snapshot",
        1440,
        "ForecastDashboard",
        '[["{week_date}","{total_pipeline}","{total_weighted}","{pct_to_quota}","{active_deal_count}","{shortfall}"]]',
      ),
      n(
        "8",
        "condition",
        "Check: Forecast shows shortfall against quota?",
        1680,
        {
          conditionDetails: {
            conditions: [
              { variable: "{shortfall}", operator: "==", value: "true" },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Weekly pipeline forecast that pulls HubSpot deals, computes weighted forecast by stage, uses AI to generate an executive narrative with risks and opportunities, emails leadership, posts highlights to Slack, updates the dashboard, and flags quota shortfalls.",
  },

  // ─── 210. Customer Invoice Thank-You Card ─────────────────────────────

  "Customer Invoice Thank-You Card": {
    nodes: [
      trigger("Stripe webhook: Invoice payment succeeded", "webhook_app", {
        source: "stripe",
        event: "invoice.payment_succeeded",
        howItStarts:
          "Fires when a Stripe invoice is successfully paid, providing the invoice ID, customer ID, amount paid, subscription ID, and payment method details.",
        setup:
          "Connect your Stripe account and enable the invoice.payment_succeeded webhook event.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "HubSpot: Lookup customer profile and account history",
        240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"stripe_customer_id","operator":"EQ","value":"{stripe_customer_id}"}]}]',
              },
              {
                key: "properties",
                value:
                  '["firstname","lastname","email","company","customer_since","total_revenue","lifecyclestage"]',
              },
            ],
            variables: [
              {
                name: "results[0].properties.firstname",
                customName: "first_name",
                type: "string",
              },
              {
                name: "results[0].properties.email",
                customName: "customer_email",
                type: "string",
              },
              {
                name: "results[0].properties.company",
                customName: "company_name",
                type: "string",
              },
              {
                name: "results[0].properties.customer_since",
                customName: "customer_since",
                type: "string",
              },
              {
                name: "results[0].properties.total_revenue",
                customName: "lifetime_revenue",
                type: "string",
              },
            ],
          },
        },
      ),
      n("3", "condition", "Check: Is this the customer's first payment?", 480, {
        conditionDetails: {
          conditions: [
            {
              variable: "{lifetime_revenue}",
              operator: "<=",
              value: "{amount_paid}",
            },
          ],
        },
      }),
      aiNode(
        "4",
        "OpenAI: Generate personalized thank-you note based on customer context",
        720,
        "You are a customer success specialist. Generate a warm, genuine thank-you message for a customer who just paid an invoice. If this is their first payment, make it extra welcoming. If they are a long-time customer, acknowledge their loyalty. Keep it personal and avoid sounding automated. Return thank_you_subject, thank_you_body, personal_note.",
        "Generate thank-you for: Name: {first_name}, Company: {company_name}, Amount: {amount_paid}, First payment: {is_first_payment}, Customer since: {customer_since}, Lifetime revenue: {lifetime_revenue}",
        [
          {
            field: "thank_you_subject",
            as: "thank_you_subject",
            type: "string",
          },
          { field: "thank_you_body", as: "thank_you_body", type: "string" },
          { field: "personal_note", as: "personal_note", type: "string" },
        ],
      ),
      emailNode(
        "5",
        "Email: Send personalized thank-you note to customer",
        960,
        "{customer_email}",
        "{thank_you_subject}",
        "{thank_you_body}",
      ),
      slackNode(
        "6",
        "Slack: Notify CSM of payment received",
        1200,
        "Payment Received\n\nCustomer: {first_name} at {company_name}\nAmount: ${amount_paid}\nFirst payment: {is_first_payment}\nLifetime revenue: ${lifetime_revenue}\n\nPersonalized thank-you sent automatically.",
      ),
      n(
        "7",
        "hitApi",
        "HubSpot: Log thank-you touchpoint on contact timeline",
        1440,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/notes",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"hs_note_body":"Auto thank-you sent for invoice payment of ${amount_paid}. {personal_note}","hs_timestamp":"{timestamp}"}',
              },
              {
                key: "associations",
                value:
                  '[{"to":{"id":"{contact_id}"},"types":[{"associationCategory":"HUBSPOT_DEFINED","associationTypeId":202}]}]',
              },
            ],
            variables: [{ name: "id", customName: "note_id", type: "string" }],
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log thank-you event to customer touchpoints",
        1680,
        "ThankYouLog",
        '[["{timestamp}","{first_name}","{company_name}","{amount_paid}","{is_first_payment}","{thank_you_subject}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Triggers on Stripe invoice payments, looks up the customer in HubSpot, checks if it is their first payment, uses AI to craft a personalized thank-you note, emails the customer, notifies the CSM in Slack, logs the touchpoint in HubSpot, and records in a spreadsheet.",
  },

  // ─── 211. Vendor Onboarding Packet ────────────────────────────────────

  "Vendor Onboarding Packet": {
    nodes: [
      trigger(
        "Webhook form: New vendor registration submitted",
        "webhook_form",
        {
          source: "form",
          event: "vendor_registration",
          howItStarts:
            "Fires when a new vendor submits the registration form, providing company name, contact details, tax ID, bank information, and compliance documents.",
          setup:
            "Create a vendor registration form with required fields: company name, contact email, tax ID, W-9/W-8 upload, bank details, and insurance certificate.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract and validate vendor details from submission",
        240,
        "You are a procurement compliance specialist. Extract the vendor company name, contact name, contact email, phone, tax ID, entity type, address, bank routing and account (last 4 only), insurance expiry date, and W-9 status from the form submission. Flag any missing or invalid fields. Return vendor_name, contact_name, contact_email, tax_id, entity_type, address, w9_status, insurance_expiry, missing_fields, all_docs_complete.",
        "Extract and validate vendor registration: {webhook_payload}",
        [
          { field: "vendor_name", as: "vendor_name", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "tax_id", as: "tax_id", type: "string" },
          { field: "w9_status", as: "w9_status", type: "string" },
          {
            field: "all_docs_complete",
            as: "all_docs_complete",
            type: "boolean",
          },
          { field: "missing_fields", as: "missing_fields", type: "string" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Validate tax and compliance documentation completeness",
        480,
        {
          codeDetails: {
            language: "javascript",
            code: "const docs_complete = inputs.all_docs_complete; const w9 = inputs.w9_status; const missing = inputs.missing_fields; const tax_valid = inputs.tax_id && inputs.tax_id.length >= 9; return { docs_ready: docs_complete && tax_valid && w9 === 'received', needs_followup: !docs_complete || !tax_valid, missing_items: missing || 'None', validation_status: docs_complete && tax_valid ? 'approved' : 'incomplete' };",
          },
        },
      ),
      n(
        "4",
        "condition",
        "Check: All required documents received and valid?",
        720,
        {
          conditionDetails: {
            conditions: [
              { variable: "{docs_ready}", operator: "==", value: "true" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Email: Send welcome packet and payment setup instructions to vendor",
        960,
        "{contact_email}",
        "Welcome to our vendor network — {vendor_name}",
        "Hello {contact_name},\n\nWelcome! Your vendor registration for {vendor_name} has been received and validated.\n\nNext steps:\n1. Your vendor profile has been created in our accounting system\n2. Payment terms: Net 30 from invoice receipt\n3. Submit invoices to: invoices@company.com\n4. Include your vendor ID ({vendor_id}) on all invoices\n\nVendor portal access: {portal_url}\n\nIf you have questions, contact our procurement team at procurement@company.com.",
      ),
      slackNode(
        "6",
        "Slack: Notify procurement team of new vendor approval",
        1200,
        "New Vendor Approved\n\nVendor: {vendor_name}\nContact: {contact_name} ({contact_email})\nEntity type: {entity_type}\nW-9 status: {w9_status}\nValidation: {validation_status}\n\nVendor has been added to QuickBooks.",
      ),
      n(
        "7",
        "hitApi",
        "QuickBooks: Create vendor record in accounting system",
        1440,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://quickbooks.api.intuit.com/v3/company/{realm_id}/vendor",
            headers: [
              { key: "Authorization", value: "Bearer {quickbooks_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "DisplayName", value: "{vendor_name}" },
              {
                key: "PrimaryEmailAddr",
                value: '{"Address":"{contact_email}"}',
              },
              { key: "CompanyName", value: "{vendor_name}" },
              { key: "TaxIdentifier", value: "{tax_id}" },
            ],
            variables: [
              { name: "Vendor.Id", customName: "vendor_id", type: "string" },
            ],
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log vendor onboarding to master vendor register",
        1680,
        "VendorRegister",
        '[["{timestamp}","{vendor_name}","{contact_email}","{tax_id}","{entity_type}","{validation_status}","{vendor_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes new vendor registration forms, uses AI to extract and validate company details and tax documents, checks compliance completeness, sends a welcome packet to approved vendors, creates a QuickBooks vendor record, notifies procurement via Slack, and logs to the vendor register.",
  },

  // ─── 212. Contract Renewal Reminder ───────────────────────────────────

  "Contract Renewal Reminder": {
    nodes: [
      trigger("Scheduled: Daily contract renewal horizon check", "schedule", {
        schedule: "Every business day at 8:00 AM",
        howItStarts:
          "Runs daily to scan HubSpot for contracts expiring within 90 days, assess risk based on account health, and proactively alert account owners and legal.",
        setup:
          "Connect HubSpot CRM with deal properties including contract_end_date, annual_value, and health_score. Configure Slack channels for legal and account team alerts.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "HubSpot: Fetch contracts expiring within 90 days",
        240,
        {
          hitApiDetails: {
            method: "GET",
            url: 'https://api.hubapi.com/crm/v3/objects/deals?properties=dealname,amount,contract_end_date,hubspot_owner_id,health_score,associated_company&filterGroups=[{"filters":[{"propertyName":"contract_end_date","operator":"BETWEEN","value":"{today}","highValue":"{today_plus_90}"}]}]&limit=200',
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "results",
                customName: "expiring_contracts",
                type: "object",
              },
              { name: "total", customName: "contract_count", type: "number" },
            ],
          },
        },
      ),
      n("3", "runCode", "Compute renewal risk score per contract", 480, {
        codeDetails: {
          language: "javascript",
          code: "const contracts = inputs.expiring_contracts; const results = []; for (const c of contracts) { const days_left = Math.ceil((new Date(c.contract_end_date) - new Date()) / 86400000); const health = c.health_score || 50; const value = c.amount; const risk = health < 40 ? 'high' : health < 70 ? 'medium' : 'low'; const is_high_value = value > 50000; results.push({ deal_name: c.dealname, amount: value, days_left, health, risk, is_high_value, owner_id: c.hubspot_owner_id }); } const high_risk = results.filter(r => r.risk === 'high'); return { contracts: results, high_risk_count: high_risk.length, has_high_value_at_risk: high_risk.some(r => r.is_high_value), total_at_risk_value: high_risk.reduce((s, r) => s + r.amount, 0) };",
        },
      }),
      n("4", "condition", "Check: High-value contract at renewal risk?", 720, {
        conditionDetails: {
          conditions: [
            {
              variable: "{has_high_value_at_risk}",
              operator: "==",
              value: "true",
            },
          ],
        },
      }),
      slackNode(
        "5",
        "Slack: Alert legal and account owner about high-risk renewals",
        960,
        "High-Risk Contract Renewal Alert\n\n{high_risk_count} contract(s) at risk of non-renewal.\nTotal at-risk value: ${total_at_risk_value}\n\nTop priority:\n{high_risk_summary}\n\nPlease initiate renewal conversations immediately.",
      ),
      emailNode(
        "6",
        "Email: Send renewal prep brief to account owner",
        1200,
        "{account_owner_email}",
        "Contract Renewal Prep — {deal_name} expires in {days_left} days",
        "Hello,\n\nThe following contract is approaching renewal and needs your attention:\n\nAccount: {deal_name}\nContract value: ${amount}\nExpiration: {contract_end_date} ({days_left} days)\nHealth score: {health}/100\nRisk level: {risk}\n\nRecommended actions:\n1. Schedule a renewal discussion with the customer\n2. Review usage and satisfaction data\n3. Prepare any pricing adjustments\n4. Coordinate with legal on contract terms\n\nPlease update the renewal status in HubSpot.",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log renewal pipeline to tracking sheet",
        1440,
        "RenewalPipeline",
        '[["{timestamp}","{deal_name}","{amount}","{days_left}","{health}","{risk}","{account_owner}"]]',
      ),
      n("8", "hitApi", "HubSpot: Create renewal task for account owner", 1680, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"hs_task_subject":"Renewal prep: {deal_name} — {days_left} days remaining","hs_task_body":"Contract value: ${amount}. Health: {health}. Risk: {risk}. Schedule renewal conversation.","hs_task_status":"NOT_STARTED","hs_task_priority":"HIGH","hubspot_owner_id":"{owner_id}"}',
            },
          ],
          variables: [{ name: "id", customName: "task_id", type: "string" }],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Daily scan for contracts expiring within 90 days in HubSpot, computes risk scores based on health and value, alerts Slack for high-risk renewals, sends prep briefs to account owners, logs to a renewal pipeline tracker, and creates HubSpot tasks.",
  },

  // ─── 213. Price Exception Approval ────────────────────────────────────

  "Price Exception Approval": {
    nodes: [
      trigger("HubSpot webhook: Deal discount exceeds 20%", "webhook_app", {
        source: "hubspot",
        event: "deal.propertyChange",
        howItStarts:
          "Fires when a HubSpot deal's discount percentage exceeds 20%, providing the deal ID, discount amount, deal value, rep ID, and associated company for approval routing.",
        setup:
          "Connect HubSpot and configure the webhook to fire when the discount_percentage property changes to a value above 20.",
        inputFields: [],
      }),
      n(
        "2",
        "runCode",
        "Extract discount details and compute revenue impact",
        240,
        {
          codeDetails: {
            language: "javascript",
            code: "const deal_amount = inputs.deal_amount; const discount_pct = inputs.discount_percentage; const list_price = inputs.list_price || deal_amount / (1 - discount_pct / 100); const discount_amount = (list_price * discount_pct / 100).toFixed(2); const net_price = (list_price - discount_amount).toFixed(2); const requires_vp = discount_pct > 30; return { deal_name: inputs.deal_name, list_price: list_price.toFixed(2), discount_pct, discount_amount, net_price, requires_vp, rep_name: inputs.rep_name, approval_level: requires_vp ? 'VP Sales + Finance' : 'Sales Manager' };",
          },
        },
      ),
      n(
        "3",
        "condition",
        "Check: Discount exceeds 30% requiring VP approval?",
        480,
        {
          conditionDetails: {
            conditions: [
              { variable: "{requires_vp}", operator: "==", value: "true" },
            ],
          },
        },
      ),
      n(
        "4",
        "approvalGate",
        "Approval: VP Sales and Finance review deep discount",
        720,
        {
          approvalDetails: {
            approvers: ["vp_sales", "finance_director"],
            instructions:
              "Price exception request requires VP + Finance approval.\n\nDeal: {deal_name}\nRep: {rep_name}\nList price: ${list_price}\nDiscount: {discount_pct}% (${discount_amount})\nNet price: ${net_price}\n\nThis exceeds the 30% threshold. Please review the business case and approve or reject.",
          },
        },
      ),
      n(
        "5",
        "hitApi",
        "HubSpot: Update deal approval status and pricing notes",
        960,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.hubapi.com/crm/v3/objects/deals/{deal_id}",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"pricing_approval_status":"approved","pricing_approved_by":"{approver_name}","pricing_approved_date":"{timestamp}","pricing_notes":"Discount of {discount_pct}% approved at {approval_level} level."}',
              },
            ],
            variables: [{ name: "id", customName: "deal_id", type: "string" }],
          },
        },
      ),
      slackNode(
        "6",
        "Slack: Notify rep of pricing approval decision",
        1200,
        "Price Exception {approval_decision}\n\nDeal: {deal_name}\nDiscount: {discount_pct}% (${discount_amount})\nNet price: ${net_price}\nApproval level: {approval_level}\nApproved by: {approver_name}\n\nRep: {rep_name} — you may proceed with this pricing.",
      ),
      emailNode(
        "7",
        "Email: Send approval confirmation with terms to rep",
        1440,
        "{rep_email}",
        "Price Exception Approved: {deal_name} — {discount_pct}% discount",
        "Hello {rep_name},\n\nYour price exception request has been approved.\n\nDeal: {deal_name}\nList price: ${list_price}\nApproved discount: {discount_pct}% (${discount_amount})\nNet price: ${net_price}\nApproved by: {approver_name}\n\nPlease ensure the customer proposal reflects these exact terms. This approval is valid for 30 days.\n\nNote: Any further discount adjustments will require a new approval.",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log price exception to governance audit trail",
        1680,
        "PriceExceptions",
        '[["{timestamp}","{deal_name}","{rep_name}","{list_price}","{discount_pct}","{net_price}","{approval_level}","{approver_name}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "5", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Triggers when a HubSpot deal discount exceeds 20%, computes revenue impact, routes discounts over 30% to VP Sales and Finance for approval, updates the deal status in HubSpot, notifies the rep via Slack and email, and logs to a price exception audit trail.",
  },

  // ─── 214. Procurement Intake Triage ───────────────────────────────────

  "Procurement Intake Triage": {
    nodes: [
      trigger("Webhook form: Procurement request submitted", "webhook_form", {
        source: "form",
        event: "procurement_request",
        howItStarts:
          "Fires when an employee submits a procurement request form, providing the requested item/service, estimated cost, business justification, urgency, and department.",
        setup:
          "Create a procurement intake form with fields: item/service description, estimated cost, vendor (if known), business justification, urgency level, and department.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Classify procurement category and route appropriately",
        240,
        "You are a procurement operations specialist. Classify the procurement request into a category (software, hardware, services, facilities, marketing), determine routing priority (standard, expedited, sole-source), extract the estimated cost, department, requestor, business justification, and preferred vendor. Flag if this appears to be a sole-source request. Return category, routing_priority, estimated_cost, department, requestor_name, requestor_email, business_justification, preferred_vendor, is_sole_source.",
        "Classify and route this procurement request: {webhook_payload}",
        [
          { field: "category", as: "category", type: "string" },
          { field: "routing_priority", as: "routing_priority", type: "string" },
          { field: "estimated_cost", as: "estimated_cost", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "requestor_name", as: "requestor_name", type: "string" },
          { field: "requestor_email", as: "requestor_email", type: "string" },
          { field: "is_sole_source", as: "is_sole_source", type: "boolean" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Route request by value and category to appropriate approver",
        480,
        {
          codeDetails: {
            language: "javascript",
            code: "const cost = parseFloat(inputs.estimated_cost); const category = inputs.category; const sole_source = inputs.is_sole_source; const needs_manager = cost > 1000; const needs_finance = cost > 5000; const needs_legal = category === 'services' && cost > 10000; return { needs_manager, needs_finance, needs_legal, sole_source_review: sole_source, approval_chain: [needs_manager && 'manager', needs_finance && 'finance', needs_legal && 'legal'].filter(Boolean).join(' > ') || 'auto-approved', ticket_priority: cost > 10000 ? 'high' : cost > 1000 ? 'medium' : 'low' };",
          },
        },
      ),
      n("4", "condition", "Check: Is this a sole-source procurement?", 720, {
        conditionDetails: {
          conditions: [
            { variable: "{sole_source_review}", operator: "==", value: "true" },
          ],
        },
      }),
      n(
        "5",
        "approvalGate",
        "Approval: Procurement manager reviews sole-source justification",
        960,
        {
          approvalDetails: {
            approvers: ["procurement_manager"],
            instructions:
              "Sole-source procurement request requires review.\n\nRequestor: {requestor_name} ({department})\nItem: {category}\nEstimated cost: ${estimated_cost}\nVendor: {preferred_vendor}\nJustification: {business_justification}\n\nPlease verify the sole-source justification meets policy requirements.",
          },
        },
      ),
      n(
        "6",
        "hitApi",
        "Jira: Create procurement ticket with routing and approval chain",
        1200,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://your-domain.atlassian.net/rest/api/3/issue",
            headers: [
              { key: "Authorization", value: "Basic {jira_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "fields",
                value:
                  '{"project":{"key":"PROC"},"summary":"[{category}] {requestor_name}: ${estimated_cost} procurement request","issuetype":{"name":"Task"},"priority":{"name":"{ticket_priority}"},"description":{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"type":"text","text":"Category: {category}\\nEstimated cost: ${estimated_cost}\\nDepartment: {department}\\nApproval chain: {approval_chain}\\nSole source: {is_sole_source}\\nJustification: {business_justification}"}]}]}}',
              },
            ],
            variables: [
              { name: "key", customName: "ticket_key", type: "string" },
              { name: "id", customName: "ticket_id", type: "string" },
            ],
          },
        },
      ),
      emailNode(
        "7",
        "Email: Confirm procurement request receipt to requestor",
        1440,
        "{requestor_email}",
        "Procurement Request Received — {ticket_key}",
        "Hello {requestor_name},\n\nYour procurement request has been received and is being processed.\n\nTicket: {ticket_key}\nCategory: {category}\nEstimated cost: ${estimated_cost}\nPriority: {ticket_priority}\nApproval chain: {approval_chain}\n\nYou will be notified as the request moves through approvals. Track your request in Jira: {jira_url}/{ticket_key}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log procurement request to intake register",
        1680,
        "ProcurementIntake",
        '[["{timestamp}","{requestor_name}","{department}","{category}","{estimated_cost}","{routing_priority}","{is_sole_source}","{ticket_key}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes procurement request forms, uses AI to classify category and routing, checks for sole-source requests requiring extra review, routes through appropriate approval chains, creates a Jira procurement ticket, confirms receipt to the requestor, and logs to the intake register.",
  },

  // ─── 215. Billing Dispute Case Workflow ───────────────────────────────

  "Billing Dispute Case Workflow": {
    nodes: [
      trigger(
        "Zendesk webhook: Ticket tagged as billing dispute",
        "webhook_app",
        {
          source: "zendesk",
          event: "ticket.tag_added",
          howItStarts:
            "Fires when a Zendesk support ticket is tagged with 'billing-dispute', providing the ticket ID, customer email, subject, description, and associated tags.",
          setup:
            "Connect Zendesk and configure the webhook to fire when the 'billing-dispute' tag is added to a ticket.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "Stripe: Fetch the disputed invoice and payment history",
        240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.stripe.com/v1/invoices?customer={stripe_customer_id}&limit=10",
            headers: [
              { key: "Authorization", value: "Bearer {stripe_secret_key}" },
              {
                key: "Content-Type",
                value: "application/x-www-form-urlencoded",
              },
            ],
            body: [],
            variables: [
              { name: "data", customName: "invoices", type: "object" },
              {
                name: "data[0].id",
                customName: "latest_invoice_id",
                type: "string",
              },
              {
                name: "data[0].amount_paid",
                customName: "amount_paid",
                type: "number",
              },
              {
                name: "data[0].status",
                customName: "invoice_status",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "runCode",
        "Validate dispute claim against invoice and payment records",
        480,
        {
          codeDetails: {
            language: "javascript",
            code: "const invoices = inputs.invoices; const dispute_amount = inputs.dispute_amount; const customer_claim = inputs.dispute_description; const invoice = invoices[0]; const amount_matches = Math.abs(invoice.amount_paid / 100 - dispute_amount) < 0.01; const is_recent = (Date.now() - invoice.created * 1000) < 90 * 86400000; const valid_dispute = amount_matches || is_recent; return { valid_dispute, amount_matches, is_recent, invoice_id: invoice.id, invoice_amount: (invoice.amount_paid / 100).toFixed(2), dispute_amount, days_since_charge: Math.ceil((Date.now() - invoice.created * 1000) / 86400000) };",
          },
        },
      ),
      n("4", "condition", "Check: Is the billing dispute claim valid?", 720, {
        conditionDetails: {
          conditions: [
            { variable: "{valid_dispute}", operator: "==", value: "true" },
          ],
        },
      }),
      n(
        "5",
        "approvalGate",
        "Approval: Finance team reviews dispute resolution",
        960,
        {
          approvalDetails: {
            approvers: ["finance_team"],
            instructions:
              "Billing dispute case requires finance review.\n\nCustomer: {customer_email}\nDisputed amount: ${dispute_amount}\nInvoice: {invoice_id} (${invoice_amount})\nDays since charge: {days_since_charge}\nClaim valid: {valid_dispute}\n\nOptions: Full refund, partial credit, or deny with explanation.",
          },
        },
      ),
      n("6", "hitApi", "Stripe: Process approved adjustment or refund", 1200, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.stripe.com/v1/refunds",
          headers: [
            { key: "Authorization", value: "Bearer {stripe_secret_key}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "charge", value: "{charge_id}" },
            { key: "amount", value: "{refund_amount_cents}" },
            { key: "reason", value: "requested_by_customer" },
          ],
          variables: [
            { name: "id", customName: "refund_id", type: "string" },
            { name: "amount", customName: "refunded_amount", type: "number" },
            { name: "status", customName: "refund_status", type: "string" },
          ],
        },
      }),
      emailNode(
        "7",
        "Email: Send dispute resolution notification to customer",
        1440,
        "{customer_email}",
        "Billing Dispute Resolved — Ticket #{ticket_id}",
        "Hello,\n\nWe have reviewed your billing dispute regarding Invoice #{invoice_id} for ${invoice_amount}.\n\nResolution: A {resolution_type} of ${refunded_amount_display} has been processed to your payment method on file.\n\nRefund ID: {refund_id}\nExpected timeline: 5-10 business days\n\nIf you have further questions, please reference Ticket #{ticket_id}.\n\nWe apologize for any inconvenience.",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log dispute case to billing audit trail",
        1680,
        "BillingDisputes",
        '[["{timestamp}","{customer_email}","{invoice_id}","{dispute_amount}","{refund_id}","{refund_status}","{resolution_type}","{ticket_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Handles billing dispute tickets from Zendesk by fetching Stripe invoice history, validating the dispute claim, routing to finance for approval, processing refunds or credits in Stripe, notifying the customer of resolution, and logging to an audit trail.",
  },

  // ─── 216. Refund Win-Back After Support ───────────────────────────────

  "Refund Win-Back After Support": {
    nodes: [
      trigger("Stripe webhook: Refund processed successfully", "webhook_app", {
        source: "stripe",
        event: "charge.refunded",
        howItStarts:
          "Fires when a Stripe refund is successfully processed, providing the charge ID, refund amount, customer ID, refund reason, and original invoice details.",
        setup:
          "Connect your Stripe account and enable the charge.refunded webhook event.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "HubSpot: Lookup customer profile, LTV, and engagement history",
        240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"stripe_customer_id","operator":"EQ","value":"{stripe_customer_id}"}]}]',
              },
              {
                key: "properties",
                value:
                  '["firstname","lastname","email","total_revenue","lifecyclestage","last_activity_date"]',
              },
            ],
            variables: [
              {
                name: "results[0].properties.firstname",
                customName: "first_name",
                type: "string",
              },
              {
                name: "results[0].properties.email",
                customName: "customer_email",
                type: "string",
              },
              {
                name: "results[0].properties.total_revenue",
                customName: "lifetime_value",
                type: "string",
              },
            ],
          },
        },
      ),
      n("3", "delayAction", "Wait 14 days for the customer to cool down", 480, {
        delayDetails: { duration: 14, unit: "days" },
      }),
      aiNode(
        "4",
        "OpenAI: Generate personalized win-back message based on refund context",
        720,
        "You are a customer retention specialist. Given a customer who recently received a refund, draft a thoughtful win-back email that acknowledges their experience, highlights improvements or value they may have missed, and includes a personalized incentive. Do not be pushy. Return subject_line, winback_body, incentive_offer, tone_assessment.",
        "Draft win-back for: Name: {first_name}, Refund amount: {refund_amount}, Refund reason: {refund_reason}, LTV: {lifetime_value}, Product: {product_name}",
        [
          { field: "subject_line", as: "winback_subject", type: "string" },
          { field: "winback_body", as: "winback_body", type: "string" },
          { field: "incentive_offer", as: "incentive_offer", type: "string" },
        ],
      ),
      n("5", "condition", "Check: Customer lifetime value exceeds $500?", 960, {
        conditionDetails: {
          conditions: [
            { variable: "{lifetime_value}", operator: ">", value: "500" },
          ],
        },
      }),
      n(
        "6",
        "approvalGate",
        "Approval: CSM reviews win-back offer for high-LTV customer",
        1200,
        {
          approvalDetails: {
            approvers: ["csm"],
            instructions:
              "Win-back offer for high-value refunded customer.\n\nCustomer: {first_name} ({customer_email})\nRefund: ${refund_amount}\nLTV: ${lifetime_value}\nIncentive: {incentive_offer}\n\nReview and approve the win-back email before sending.",
          },
        },
      ),
      emailNode(
        "7",
        "Email: Send approved win-back offer to customer",
        1440,
        "{customer_email}",
        "{winback_subject}",
        "{winback_body}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log win-back attempt to retention tracker",
        1680,
        "WinbackTracker",
        '[["{timestamp}","{first_name}","{customer_email}","{refund_amount}","{lifetime_value}","{incentive_offer}","{winback_status}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Triggers on Stripe refunds, looks up the customer in HubSpot, waits 14 days, uses AI to craft a personalized win-back message, routes high-LTV customers through CSM approval, sends the win-back offer, and tracks results in a retention spreadsheet.",
  },

  // ─── 217. Service Credit Calculator ───────────────────────────────────

  "Service Credit Calculator": {
    nodes: [
      trigger("PagerDuty webhook: Incident resolved", "webhook_app", {
        source: "pagerduty",
        event: "incident.resolved",
        howItStarts:
          "Fires when a PagerDuty incident is resolved, providing the incident ID, service name, severity, created_at, resolved_at, and affected customers for SLA credit calculation.",
        setup:
          "Connect PagerDuty and enable the incident.resolved webhook. Configure your SLA terms (e.g., 99.9% uptime = max 43.8 min/month downtime).",
        inputFields: [],
      }),
      n(
        "2",
        "runCode",
        "Compute total downtime duration from incident timestamps",
        240,
        {
          codeDetails: {
            language: "javascript",
            code: "const created = new Date(inputs.incident_created_at); const resolved = new Date(inputs.incident_resolved_at); const downtime_ms = resolved - created; const downtime_minutes = Math.ceil(downtime_ms / 60000); const downtime_hours = (downtime_minutes / 60).toFixed(2); const service = inputs.service_name; const severity = inputs.severity; return { downtime_minutes, downtime_hours, service, severity, incident_id: inputs.incident_id };",
          },
        },
      ),
      n(
        "3",
        "runCode",
        "Apply SLA credit rules based on downtime and service tier",
        480,
        {
          codeDetails: {
            language: "javascript",
            code: "const minutes = inputs.downtime_minutes; const severity = inputs.severity; const monthly_fee = inputs.customer_monthly_fee || 1000; const sla_target = 99.9; const allowed_downtime = 43.8; const excess = Math.max(0, minutes - allowed_downtime); const credit_pct = Math.min(excess * 0.5, 30); const credit_amount = (monthly_fee * credit_pct / 100).toFixed(2); const owes_credit = parseFloat(credit_amount) > 0; return { excess_downtime: excess.toFixed(1), credit_pct: credit_pct.toFixed(1), credit_amount, owes_credit, monthly_fee, sla_target };",
          },
        },
      ),
      n("4", "condition", "Check: SLA credit amount exceeds $0?", 720, {
        conditionDetails: {
          conditions: [
            { variable: "{owes_credit}", operator: "==", value: "true" },
          ],
        },
      }),
      n(
        "5",
        "approvalGate",
        "Approval: Finance reviews SLA credit before application",
        960,
        {
          approvalDetails: {
            approvers: ["finance_team"],
            instructions:
              "SLA Credit Approval Required\n\nIncident: {incident_id}\nService: {service}\nDowntime: {downtime_hours} hours ({downtime_minutes} min)\nExcess beyond SLA: {excess_downtime} min\nCredit: {credit_pct}% of monthly fee = ${credit_amount}\n\nApprove to apply the credit to the customer's Stripe account.",
          },
        },
      ),
      emailNode(
        "6",
        "Email: Notify customer of SLA credit applied to their account",
        1200,
        "{customer_email}",
        "Service Credit Applied — Incident #{incident_id}",
        "Hello,\n\nWe are writing regarding the service disruption on {incident_date} affecting {service}.\n\nIncident duration: {downtime_hours} hours\nSLA target: {sla_target}% uptime\nCredit applied: ${credit_amount} ({credit_pct}% of your monthly fee)\n\nThis credit will appear on your next invoice. We sincerely apologize for the disruption and have taken steps to prevent recurrence.\n\nIncident post-mortem: {postmortem_url}\n\nThank you for your patience.",
      ),
      n(
        "7",
        "hitApi",
        "Stripe: Apply credit to customer's next invoice",
        1440,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.stripe.com/v1/customers/{stripe_customer_id}/balance_transactions",
            headers: [
              { key: "Authorization", value: "Bearer {stripe_secret_key}" },
              {
                key: "Content-Type",
                value: "application/x-www-form-urlencoded",
              },
            ],
            body: [
              { key: "amount", value: "-{credit_amount_cents}" },
              { key: "currency", value: "usd" },
              {
                key: "description",
                value:
                  "SLA credit for incident {incident_id}: {downtime_hours}h downtime",
              },
            ],
            variables: [
              { name: "id", customName: "balance_txn_id", type: "string" },
            ],
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log SLA credit to compliance timeline",
        1680,
        "SLACredits",
        '[["{timestamp}","{incident_id}","{service}","{downtime_minutes}","{credit_pct}","{credit_amount}","{customer_email}","{balance_txn_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Triggers on PagerDuty incident resolution, computes downtime and applies SLA credit rules, routes credits through finance approval, notifies the customer, applies the credit to their Stripe balance, and logs to a compliance timeline.",
  },

  // ─── 218. PO Created to Slack Alert + Docs ────────────────────────────

  "PO Created to Slack Alert + Docs": {
    nodes: [
      trigger("QuickBooks webhook: Purchase order created", "webhook_app", {
        source: "quickbooks",
        event: "purchase_order.create",
        howItStarts:
          "Fires when a new purchase order is created in QuickBooks, providing the PO number, vendor name, total amount, line items, and created-by user.",
        setup:
          "Connect your QuickBooks account and enable the purchase_order.create webhook event.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract PO details and generate vendor communication",
        240,
        "You are a procurement operations assistant. Extract the PO number, vendor name, vendor email, total amount, line items with quantities and unit prices, delivery date, shipping address, and payment terms from the QuickBooks PO event. Generate a professional vendor notification email. Return po_number, vendor_name, vendor_email, total_amount, line_items_summary, delivery_date, payment_terms, vendor_email_body.",
        "Extract PO details from this QuickBooks purchase order: {webhook_payload}",
        [
          { field: "po_number", as: "po_number", type: "string" },
          { field: "vendor_name", as: "vendor_name", type: "string" },
          { field: "vendor_email", as: "vendor_email", type: "string" },
          { field: "total_amount", as: "total_amount", type: "string" },
          {
            field: "line_items_summary",
            as: "line_items_summary",
            type: "string",
          },
          { field: "delivery_date", as: "delivery_date", type: "string" },
          {
            field: "vendor_email_body",
            as: "vendor_email_body",
            type: "string",
          },
        ],
      ),
      n("3", "runCode", "Validate PO against department budget", 480, {
        codeDetails: {
          language: "javascript",
          code: "const amount = parseFloat(inputs.total_amount); const dept_budget_remaining = inputs.dept_budget_remaining || 50000; const within_budget = amount <= dept_budget_remaining; const is_large_po = amount > 5000; const budget_after = (dept_budget_remaining - amount).toFixed(2); return { within_budget, is_large_po, budget_remaining: dept_budget_remaining, budget_after, budget_utilization_pct: ((1 - budget_after / dept_budget_remaining) * 100).toFixed(1) };",
        },
      }),
      slackNode(
        "4",
        "Slack: Alert procurement team of new purchase order",
        720,
        "New Purchase Order Created\n\nPO#: {po_number}\nVendor: {vendor_name}\nAmount: ${total_amount}\nDelivery date: {delivery_date}\nItems: {line_items_summary}\n\nBudget remaining: ${budget_after}\nCreated by: {created_by}",
      ),
      n("5", "hitApi", "Google Drive: Create PO documentation folder", 960, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/drive/v3/files",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "PO-{po_number} — {vendor_name}" },
            { key: "mimeType", value: "application/vnd.google-apps.folder" },
            { key: "parents", value: '["{po_folder_id}"]' },
          ],
          variables: [
            { name: "id", customName: "po_folder_id_new", type: "string" },
            { name: "webViewLink", customName: "folder_url", type: "string" },
          ],
        },
      }),
      emailNode(
        "6",
        "Email: Send PO confirmation and details to vendor",
        1200,
        "{vendor_email}",
        "Purchase Order #{po_number} — {vendor_name}",
        "{vendor_email_body}",
      ),
      n(
        "7",
        "condition",
        "Check: PO amount exceeds $5K requiring finance visibility?",
        1440,
        {
          conditionDetails: {
            conditions: [
              { variable: "{is_large_po}", operator: "==", value: "true" },
            ],
          },
        },
      ),
      slackNode(
        "8",
        "Slack: Alert #finance about large purchase order",
        1680,
        "Large PO Alert\n\nPO#: {po_number} for ${total_amount} to {vendor_name}\n\nThis exceeds the $5K visibility threshold.\nBudget impact: ${budget_after} remaining after this PO.\nDocumentation: {folder_url}\n\nPlease review if needed.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Triggers on QuickBooks PO creation, uses AI to extract details and generate vendor communication, validates against department budget, alerts the procurement team in Slack, creates a Google Drive documentation folder, emails the vendor, and escalates large POs to finance.",
  },
};
